import React from 'react';
import { Button } from "@/components/ui/button";
import { Lightbulb, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SystemPromptExample {
  title: string;
  prompt: string;
  description: string;
}

interface SystemPromptGuidanceProps {
  onSelectExample: (prompt: string) => void;
}



export function SystemPromptGuidance({ onSelectExample }: SystemPromptGuidanceProps) {
  const { t } = useTranslation('business', { keyPrefix: 'communication' });
  
  const examples: SystemPromptExample[] = [
    {
      title: t('wildlifeTourCompany'),
      prompt: "You are a helpful wildlife tour guide assistant for [Your Company Name]. You specialize in wildlife tours, animal encounters, and nature experiences. You know about local wildlife, tour packages, safety guidelines, and booking procedures. Always be enthusiastic about wildlife conservation and provide helpful, accurate information about tours and animals. If you don't know something specific, ask customers to contact our team directly.",
      description: t('wildlifeTourCompanyDesc')
    },
    {
      title: t('conservationOrganization'), 
      prompt: "You are an AI assistant for [Your Organization Name], a wildlife conservation organization. You provide information about our conservation projects, wildlife protection efforts, volunteer opportunities, and donation programs. You're passionate about wildlife conservation and can discuss various species, habitats, and conservation challenges. Always encourage visitors to get involved in conservation efforts.",
      description: t('conservationOrganizationDesc')
    },
    {
      title: t('zooOrWildlifePark'),
      prompt: "You are a friendly assistant for [Your Zoo/Park Name]. You help visitors with information about our animals, exhibits, feeding times, educational programs, and special events. You know about animal facts, conservation status, and park facilities. You're enthusiastic about wildlife education and always promote conservation awareness while helping visitors plan their visit.",
      description: t('zooOrWildlifeParkDesc')
    }
  ];
  
  return (
    <div className="space-y-4">
      {/* Why System Prompts Matter */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div>
          <h4 className="font-semibold text-blue-900 mb-1">{t('whySystemPromptsMatter')}</h4>
          <p className="text-sm text-blue-800 mb-3">
            {t('systemPromptJobDescription')}
          </p>
          <div className="text-xs text-blue-700">
            <p className="font-medium mb-1">{t('goodPromptsInclude')}</p>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li>{t('yourBusinessNameAndWhatYouDo')}</li>
              <li>{t('yourExpertiseAndServices')}</li>
              <li>{t('yourBrandPersonalityAndTone')}</li>
              <li>{t('whatToDoWhenUnsure')}</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Template Examples */}
      <div className="space-y-3">
        <h4 className="font-medium text-slate-900 flex items-center gap-2">
          <Target className="w-4 h-4 text-emerald-600" />
          {t('chooseTemplateToGetStarted')}
        </h4>
        {examples.map((example, index) => (
          <div key={index} className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h5 className="font-medium text-slate-900">{example.title}</h5>
                <p className="text-xs text-slate-600">{example.description}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onSelectExample(example.prompt)}
                className="text-xs"
              >
                {t('useTemplate')}
              </Button>
            </div>
            <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded border">
              {example.prompt.length > 150 ? example.prompt.substring(0, 150) + "..." : example.prompt}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
} 